import {
  AlertTriangle,
  ClipboardCheck,
  Clock,
  FileText,
  ShieldAlert,
  TrendingUp,
} from 'lucide-react';
import React from 'react';

import type { OrgSummary } from '@/pages/analytics/types';
import {
  formatCountDelta,
  formatDurationDelta,
  formatDurationSeconds,
  formatInt,
  formatPpDelta,
  formatRatePercent,
} from '@/pages/analytics/utils/formatters';
import { formatAggregateScore } from '@/pages/qc/utils';

import { KpiCard } from './KpiCard';

interface OrgKpiGridProps {
  summary?: OrgSummary;
  loading?: boolean;
}

// Soft blue tint for the neutral-info KPIs (Total/Evaluated Calls + Duration).
// Uses the dashboard's primary blue at 10% opacity so it picks up dark-mode
// adjustments via the theme token, without needing a new --info-* token.
const BLUE_BADGE = 'bg-primary/10 text-primary';

export const OrgKpiGrid: React.FC<OrgKpiGridProps> = ({ summary, loading }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
      <KpiCard
        icon={TrendingUp}
        iconClassName="bg-positive text-positive-foreground"
        label="Average Quality Score"
        value={summary ? formatAggregateScore(summary.average_quality_score.value) : '—'}
        delta={
          summary
            ? formatPpDelta(
                summary.average_quality_score.value,
                summary.average_quality_score.prior_value,
                'up-is-good',
              )
            : undefined
        }
        info="Weighted average of all AI quality scores across eligible evaluations in the selected period. Scores are based on the 3-section rubric (Compliance, Communication, Content)."
        loading={loading}
      />
      <KpiCard
        icon={ClipboardCheck}
        iconClassName={BLUE_BADGE}
        label="Total Calls"
        value={summary ? formatInt(summary.total_calls.value) : '—'}
        delta={
          summary
            ? formatCountDelta(
                summary.total_calls.value,
                summary.total_calls.prior_value,
                'up-is-good',
              )
            : undefined
        }
        info="Total number of calls received in the selected period, including both evaluated and ineligible calls (voicemails, wrong numbers, dropped calls, etc.)."
        loading={loading}
      />
      <KpiCard
        icon={FileText}
        iconClassName={BLUE_BADGE}
        label="Evaluated Calls"
        value={summary ? formatInt(summary.evaluated_calls.value) : '—'}
        delta={
          summary
            ? formatCountDelta(
                summary.evaluated_calls.value,
                summary.evaluated_calls.prior_value,
                'up-is-good',
              )
            : undefined
        }
        info="Calls that were scored by the QC system. Excludes ineligible calls (voicemails, wrong numbers, dropped calls, test calls, calls under 30s)."
        loading={loading}
      />
      <KpiCard
        icon={Clock}
        iconClassName={BLUE_BADGE}
        label="Average Call Duration"
        value={summary ? formatDurationSeconds(summary.average_call_duration_seconds.value) : '—'}
        delta={
          summary
            ? formatDurationDelta(
                summary.average_call_duration_seconds.value,
                summary.average_call_duration_seconds.prior_value,
                'down-is-good',
              )
            : undefined
        }
        info="Average duration of evaluated calls in the selected period, measured from call start to call end. Lower times may indicate efficiency; higher times may signal complexity."
        loading={loading}
      />
      <KpiCard
        icon={AlertTriangle}
        iconClassName="bg-attention text-attention-foreground"
        label="Escalation Rate"
        value={summary ? formatRatePercent(summary.escalation_rate.value) : '—'}
        delta={
          summary
            ? formatPpDelta(
                summary.escalation_rate.value,
                summary.escalation_rate.prior_value,
                'down-is-good',
              )
            : undefined
        }
        info="Percentage of calls flagged as requiring escalation by the AI triage system. Includes calls classified as needing supervisor review or follow-up action."
        loading={loading}
      />
      <KpiCard
        icon={ShieldAlert}
        iconClassName="bg-critical text-critical-foreground"
        label="Compliance Failure Rate"
        value={summary ? formatRatePercent(summary.compliance_failure_rate.value) : '—'}
        delta={
          summary
            ? formatPpDelta(
                summary.compliance_failure_rate.value,
                summary.compliance_failure_rate.prior_value,
                'down-is-good',
              )
            : undefined
        }
        info="Percentage of calls where the AI detected at least one critical compliance question failure (e.g., HIPAA verification, AE/PQC reporting). A failing answer on any critical question triggers this flag."
        loading={loading}
      />
    </div>
  );
};
